import { useState, useEffect, useRef } from "react";
import { Button } from "../ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import api from "../../lib/api";
import { Input } from "../ui/input";
import { useToast } from "../../hooks/use-toast";
import { CustomSelect } from "../ui/custom-select";
import { Download, XIcon } from "lucide-react";
import Pagination from "../ui/pagination";
import {
  Select,
  SelectValue,
  SelectTrigger,
  SelectItem,
  SelectContent,
} from "../ui/select";
import { useAuth } from "../../lib/authContext";

export default function MappedMeds({ entities }: { entities: any }) {
  const { user, excelPermissions } = useAuth();
  const { toast } = useToast();
  const [mappedMeds, setMappedMeds] = useState([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [selectedMed, setSelectedMed] = useState<any>(null);
  const [selectedEntity, setSelectedEntity] = useState<any>(0);
  const [isDownloading, setIsDownloading] = useState(false);
  const [total, setTotal] = useState(0);
  const [searchTerm, setSearchTerm] = useState("");
  const [masterMeds, setMasterMeds] = useState([]);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!user?.id) return;
    api.getMappedMeds(page, limit, "mapped", selectedEntity, user.id).then(data => {
      setMappedMeds(data?.data || []);
      setTotal(data?.totalCount || 0);
    }).catch(err => {
      console.error("Failed to fetch mapped meds:", err);
      setMappedMeds([]);
      setTotal(0);
    });
  }, [page, limit, selectedEntity, user?.id]);

  useEffect(() => {
    if (searchTerm && selectedMed?.id && user?.id) { // Added user?.id to condition
      api
        .searchMasterMeds(searchTerm, selectedMed?.id, selectedMed?.entity_id, user.id) // Added user.id
        .then(data => {
          setMasterMeds(data?.data || []);
        }).catch(err => {
          console.error("Failed to search master meds:", err);
          setMasterMeds([]);
        });
    }
  }, [searchTerm, selectedMed]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setSearchTerm("");
      }
    }

    if (searchTerm) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [searchTerm]);

  const saveEditedMed = (med: any) => {
    api.mapMed(med.id, med.master_med_id, user?.id || "").then(data => {
      if (data?.success) {
        toast({
          title: data.message || "Medicine mapped successfully",
        });
        setSelectedMed(null);
        if (!user?.id) return;
        api.getMappedMeds(page, limit, "mapped", selectedEntity, user.id).then(data => {
          setMappedMeds(data?.data || []);
          setTotal(data?.totalCount || 0);
        });
      }
    }).catch(err => {
      console.error("Failed to map med:", err);
      toast({
        title: "Error mapping medicine",
        variant: "destructive"
      });
    });
  };

  const handleDownload = async () => {
    if (!selectedEntity) {
      toast({
        title: "Please select a pharmacy first",
        variant: "destructive",
      });
      return;
    }

    setIsDownloading(true);
    try {
      const response = await api.downloadMappedMedsExcel(selectedEntity);

      if (response.success) {
        // Convert base64 to blob
        const byteCharacters = atob(response.data);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = response.filename;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);

        toast({
          title: `Downloaded ${response.count} mapped medications`,
        });
      } else {
        toast({
          title: response.error || "Failed to download",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error downloading file",
        variant: "destructive",
      });
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSearch = (searchTerm: string, med: any) => {
    setSearchTerm(searchTerm);
  };

  const getDropdownPosition = (element: HTMLElement | null) => {
    if (!element) return "bottom";

    const rect = element.getBoundingClientRect();
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    // If there's less space below than above, show dropdown on top
    if (spaceBelow < 200 && spaceAbove > 200) {
      return "top";
    }

    // Check if element is in the bottom half of the viewport
    if (rect.top > window.innerHeight / 2) {
      return "top";
    }

    return "bottom";
  };

  useEffect(() => {
    if (searchTerm && selectedMed?.id) {
      // Force a recalculation of position after dropdown is rendered
      setTimeout(() => {
        const position = getDropdownPosition(
          document.getElementById(`med-input-${selectedMed.id}`),
        );
        // You could set this to state if needed
      }, 0);
    }
  }, [searchTerm, selectedMed?.id]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex justify-between items-center mt-2">
        <div className="w-1/3">
          <CustomSelect
            options={entities}
            value={selectedEntity}
            onChange={setSelectedEntity}
          />
        </div>
        {excelPermissions.canDownloadExcel && (
          <Button
            onClick={handleDownload}
            disabled={isDownloading || !selectedEntity}
            className="flex items-center gap-2"
          >
            <Download size={16} />
            {isDownloading ? "Downloading..." : "Download Mapped Data"}
          </Button>
        )}
      </div>
      <div className="mt-4 border rounded-md dark:border-gray-700">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Product ID</TableHead>
              <TableHead>Product Name</TableHead>
              <TableHead>Packing</TableHead>
              <TableHead>Manufacturer</TableHead>
              <TableHead>MRP</TableHead>
              <TableHead>Pharmacy Name</TableHead>
              <TableHead>Legacy Product Name</TableHead>
              <TableHead>Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mappedMeds?.length > 0 ? (
              mappedMeds.map((med: any) => (
                <TableRow key={med?.id || Math.random()}>
                  <TableCell>{med.product_id}</TableCell>
                  <TableCell>{med.product_name}</TableCell>
                  <TableCell>{med.product_packing}</TableCell>
                  <TableCell>{med.product_manufacturer}</TableCell>
                  <TableCell>₹{med.mrp}</TableCell>
                  <TableCell>{med.entity_name}</TableCell>
                  {selectedMed?.id === med.id ? (
                    <TableCell>
                      <div className="relative">
                        <Input
                          id={`med-input-${selectedMed.id}`}
                          type="text"
                          value={selectedMed.legacy_product_name}
                          onChange={e => {
                            setSelectedMed({
                              ...selectedMed,
                              legacy_product_name: e.target.value,
                            });

                            // Trigger search when input changes
                            if (e.target.value?.length > 2) {
                              handleSearch(e.target.value, med);
                            }
                          }}
                          onClick={() => {
                            // Initialize search with current value when clicked
                            if (med?.legacy_product_name?.length > 2) {
                              handleSearch(med.legacy_product_name, med);
                            }
                          }}
                        />

                        {searchTerm && selectedMed?.id && (
                          <div
                            ref={dropdownRef}
                            className={`absolute ${
                              getDropdownPosition(
                                document.getElementById(
                                  `med-input-${selectedMed.id}`,
                                ),
                              ) === "top"
                                ? "bottom-full mb-1"
                                : "top-full mt-1"
                            } z-50 w-full bg-white dark:bg-gray-800 rounded-md border border-gray-200 dark:border-gray-700 shadow-lg overflow-hidden`}
                          >
                            <div className="max-h-[200px] overflow-y-auto">
                              {masterMeds?.length === 0 ? (
                                <div className="px-4 py-2 text-sm text-gray-500">
                                  No results found.
                                </div>
                              ) : (
                                <div>
                                  {masterMeds?.map((masterMed: any) => (
                                    <div
                                      key={masterMed?.id || Math.random()}
                                      className="px-4 py-2 hover:bg-gray-100 dark:hover:bg-gray-700 cursor-pointer"
                                      onClick={() => {
                                        // Update the selected med with the new master med
                                        setSelectedMed({
                                          ...selectedMed,
                                          legacy_product_name:
                                            masterMed.drugname,
                                          master_med_id: masterMed.id,
                                        });
                                        setSearchTerm("");
                                      }}
                                      onKeyDown={e => {
                                        if (e.key === "Enter") {
                                          setSelectedMed({
                                            ...selectedMed,
                                            legacy_product_name:
                                              masterMed.drugname,
                                            master_med_id: masterMed.id,
                                          });
                                          setSearchTerm("");
                                        }
                                      }}
                                    >
                                      {masterMed.drugname} (
                                      {masterMed.drugquantity}
                                      {masterMed.drugmeasure}){" "}
                                      {masterMed.packform} - ₹{masterMed.mrp}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </TableCell>
                  ) : (
                    <TableCell>{med.legacy_product_name}</TableCell>
                  )}
                  <TableCell>
                    {selectedMed?.id === med.id ? (
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="bg-green-500 text-white"
                          onClick={() => {
                            saveEditedMed(selectedMed);
                          }}
                        >
                          Save
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            setSelectedMed(null);
                          }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setSelectedMed(med);
                        }}
                      >
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} className="text-center">
                  No mapped medications found
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      <div className="flex justify-between items-center mt-4">
        <div className="w-1/6">
          <Select
            value={limit.toString()}
            onValueChange={value => setLimit(Number(value))}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select limit" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="10">10</SelectItem>
              <SelectItem value="20">20</SelectItem>
              <SelectItem value="50">50</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-gray-500 mt-2">items per page</span>
        </div>
        <Pagination
          page={page}
          totalPages={Math.ceil(total / limit)}
          onPageChange={setPage}
        />
      </div>
    </div>
  );
}
